import React, { useState } from "react";
import PageHeader from "../components/PageHeader";
import { COMPANY } from "../mock/data";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { trackLeadConversion } from "../lib/gtag";

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    // Fire Google Ads "Submit lead form" conversion (AW-18001331429/rfD-CNj07YQcEOWJ2odD)
    trackLeadConversion();
    setTimeout(() => {
      setLoading(false);
      toast({
        title: "Message received",
        description: "Thanks! Our support team will get back to you within 24 hours."
      });
      setForm({ name: "", email: "", subject: "", message: "" });
    }, 800);
  };

  return (
    <>
      <PageHeader
        eyebrow="Contact Us"
        title="We're here to help, 24/7"
        subtitle={`Have a question about ${COMPANY.brand}? Reach out and our support team will get back to you as quickly as possible.`}
      />

      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-4">
            <InfoCard icon={Phone} title="Call Us" primary={COMPANY.phone} secondary="Talk to a real security expert" href={`tel:${COMPANY.phoneRaw}`} />
            <InfoCard icon={Mail} title="Email Support" primary={COMPANY.email} secondary="Response within 24 hours" href={`mailto:${COMPANY.email}`} />
            <InfoCard icon={MapPin} title="Office"
              primary="Our Address"
              secondary={`${COMPANY.address.line2}, ${COMPANY.address.city} ${COMPANY.address.zip}, ${COMPANY.address.country}`} />
            <InfoCard icon={Clock} title="Support Hours" primary="24 / 7 / 365" secondary="Live agents around the clock" />
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm">
              <h2 className="section-title text-2xl font-extrabold text-slate-900">Send us a message</h2>
              <p className="mt-2 text-sm text-slate-600">Fill in the form below and we&apos;ll get back to you shortly.</p>
              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-slate-700">Full name</Label>
                    <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Doe" className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-slate-700">Email address</Label>
                    <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="subject" className="text-slate-700">Subject</Label>
                  <Input id="subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="How can we help?" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="message" className="text-slate-700">Message</Label>
                  <Textarea id="message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us more about your question..." className="mt-1.5" />
                </div>
                <Button type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-6">
                  <Send className="h-4 w-4 mr-2" /> {loading ? "Sending..." : "Send message"}
                </Button>
                <p className="text-xs text-slate-500">By submitting this form you agree to our <a href="/privacy-policy" className="text-emerald-700 underline">Privacy Policy</a>.</p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function InfoCard({ icon: Icon, title, primary, secondary, href }) {
  const inner = (
    <div className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-5 hover:border-emerald-400 hover:shadow-md transition-all">
      <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{title}</p>
        <p className="mt-1 font-semibold text-slate-900">{primary}</p>
        {secondary && <p className="text-sm text-slate-600">{secondary}</p>}
      </div>
    </div>
  );
  return href ? <a href={href}>{inner}</a> : inner;
}
